import React, { useEffect, useMemo, useState } from 'react';

import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';

import { DIVIDER } from '../../../../ThemeContext/constants';
import FormSelectField from '../../../Components/CustomUIElements/FormSelectField';
import { FormTextField } from '../../../Components/CustomUIElements/FormTextField';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';
import {
  buildAvailabilityWeekSections,
  normalizeAvailabilityTableData
} from '../../availabilityTableUtils';
import { classes } from '../styles';

import type { Dayjs } from 'dayjs';

import type { IAvailabilityConfig } from '../../../Components/Utilities/QuestionFieldViewer';
import type { IAvailabilityTableData } from '../../types';

dayjs.extend(duration);
dayjs.extend(customParseFormat);

const FALLBACK_TIME = '00:00';

export const parseAvailabilityTime = (time?: string | null): Dayjs => {
  const parsedTime = dayjs(time ?? FALLBACK_TIME, 'HH:mm', true);
  return parsedTime.isValid() ? parsedTime : dayjs(FALLBACK_TIME, 'HH:mm', true);
};

const formatAvailabilityTime = (time?: Dayjs | null): string =>
  time?.isValid() ? time.format('HH:mm') : FALLBACK_TIME;

const DayRow = ({
  payrollDay,
  day,
  allowBreak,
  onChange,
  data,
  disabled = false
}: {
  payrollDay: number;
  day: string;
  allowBreak: boolean;
  onChange: (value: IAvailabilityTableData) => void;
  data: IAvailabilityTableData;
  disabled?: boolean;
}) => {
  const [fromTime, setFromTime] = React.useState<Dayjs | null>(
    parseAvailabilityTime(data?.from_time)
  );
  const [toTime, setToTime] = React.useState<Dayjs | null>(parseAvailabilityTime(data?.to_time));

  useEffect(() => {
    setFromTime(parseAvailabilityTime(data?.from_time));
    setToTime(parseAvailabilityTime(data?.to_time));
  }, [data?.from_time, data?.to_time]);

  const calculateTotalTime = ({
    from = fromTime,
    to = toTime,
    breakTime = data?.break_time
  }: {
    from?: Dayjs | null;
    to?: Dayjs | null;
    breakTime?: number;
  }) => {
    const shiftStart = dayjs(from);
    let shiftEnd = dayjs(to);

    if (shiftEnd.isBefore(shiftStart)) {
      shiftEnd = shiftEnd.add(1, 'day');
    }
    const shiftTime = dayjs.duration(shiftEnd.diff(shiftStart)).format('HH:mm');
    const timeDiff = shiftTime.split(':');
    const totalTime = Number(timeDiff[0]) + Number(timeDiff[1]) / 60 - (breakTime ?? 0) / 60;
    return Math.round(totalTime * 100) / 100;
  };

  return (
    <TableRow>
      <TableCell sx={classes.availabilityTableBody}>
        <Stack
          sx={{
            alignItems: 'center'
          }}
        >
          {payrollDay}
        </Stack>
      </TableCell>
      <TableCell sx={classes.availabilityTableBody}>
        <Stack
          sx={{
            alignItems: 'center'
          }}
        >
          {day}
        </Stack>
      </TableCell>
      <TableCell sx={classes.availabilityTableBody}>
        <Stack
          sx={{
            alignItems: 'center'
          }}
        >
          <FormTextField
            draggable={disabled}
            onChange={(event) => onChange({ ...data, heading_1: event.target.value })}
            styles={classes.tableTextfield}
            value={data?.heading_1}
          />
        </Stack>
      </TableCell>
      <TableCell sx={classes.availabilityTableBody}>
        <Stack
          sx={{
            alignItems: 'center'
          }}
        >
          <FormTextField
            draggable={disabled}
            onChange={(event) => onChange({ ...data, heading_2: event.target.value })}
            styles={classes.tableTextfield}
            value={data?.heading_2}
          />
        </Stack>
      </TableCell>
      <TableCell sx={classes.availabilityTableBody}>
        <Stack
          sx={{
            columnGap: '8px',
            flexDirection: 'row',
            justifyContent: 'space-around'
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              disableOpenPicker
              disabled={disabled}
              format="HH:mm"
              value={dayjs(fromTime)}
              views={['hours', 'minutes']}
              onChange={(newValue) => {
                const parsedTime = parseAvailabilityTime(newValue?.format('HH:mm'));
                const formattedTime = formatAvailabilityTime(parsedTime);
                setFromTime(parsedTime);
                const newData = {
                  ...data,
                  from_time: formattedTime
                };
                onChange({
                  ...newData,
                  total_time: calculateTotalTime({ from: parsedTime })
                });
              }}
              slotProps={{
                textField: {
                  variant: 'standard',

                  slotProps: {
                    input: {
                      disableUnderline: true,
                      sx: {
                        width: '70px',
                        border: `1px solid ${DIVIDER}`,
                        padding: '0px 8px',
                        margin: '2px 0px',
                        borderRadius: '6px',
                        '& .MuiInputBase-input': { padding: '6px', fontSize: '12px' }
                      }
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              disableOpenPicker
              disabled={disabled}
              format="HH:mm"
              value={dayjs(toTime)}
              views={['hours', 'minutes']}
              onChange={(newValue) => {
                const parsedTime = parseAvailabilityTime(newValue?.format('HH:mm'));
                const formattedTime = formatAvailabilityTime(parsedTime);
                setToTime(parsedTime);
                const newData = {
                  ...data,
                  to_time: formattedTime
                };
                onChange({
                  ...newData,
                  total_time: calculateTotalTime({ to: parsedTime })
                });
              }}
              slotProps={{
                textField: {
                  variant: 'standard',

                  slotProps: {
                    input: {
                      disableUnderline: true,
                      sx: {
                        width: '70px',
                        border: `1px solid ${DIVIDER}`,
                        padding: '0px 8px',
                        margin: '2px 0px',
                        borderRadius: '6px',
                        '& .MuiInputBase-input': { padding: '6px', fontSize: '12px' }
                      }
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Stack>
      </TableCell>
      {allowBreak ? (
        <TableCell sx={classes.availabilityTableBody}>
          <Stack
            sx={{
              alignItems: 'center'
            }}
          >
            <FormSelectField
              defaultValue={data?.break_time?.toString()}
              isDisabled={disabled}
              onChange={(value) => {
                const newData = { ...data, break_time: Number(value) };
                onChange({
                  ...newData,
                  total_time: calculateTotalTime({ breakTime: Number(value) })
                });
              }}
              options={[
                { value: '0', label: '0 mins' },
                { value: '15', label: '15 mins' },
                { value: '30', label: '30 mins' },
                { value: '45', label: '45 mins' },
                { value: '60', label: '1 hour' }
              ]}
              styles={{
                minWidth: '80px',
                '& .MuiInputBase-input': { fontSize: '12px', margin: 'unset' },
                '& .MuiInputBase-input.MuiSelect-select': {
                  display: 'flex',
                  height: '18px',
                  alignItems: 'center'
                }
              }}
            />
          </Stack>
        </TableCell>
      ) : null}
      <TableCell sx={classes.availabilityTableBody}>
        <Stack
          sx={{
            alignItems: 'center'
          }}
        >
          {data?.total_time}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export const buildAvailabilityPreviewState = (
  weekDuration: string | number,
  answer?: Record<string, IAvailabilityTableData>
): Record<string, IAvailabilityTableData> => {
  let normalizedWeekDuration: string | undefined;
  if (typeof weekDuration === 'number') {
    normalizedWeekDuration = weekDuration > 7 ? 'fortnightly' : 'weekly';
  } else {
    normalizedWeekDuration = weekDuration;
  }

  return normalizeAvailabilityTableData(answer ?? {}, normalizedWeekDuration);
};

const AvailabilityPreview = ({
  availabilityConfig,
  onChange,
  answer,
  disabled = false,
  error,
  innerRef,
  removePadding
}: {
  availabilityConfig: IAvailabilityConfig;
  onChange?: (value: Record<string, IAvailabilityTableData>) => void;
  answer?: Record<string, IAvailabilityTableData>;
  disabled?: boolean;
  error?: string;
  innerRef?: React.Ref<HTMLDivElement>;
  removePadding?: boolean;
}): React.JSX.Element => {
  const normalizedInitialState = useMemo(
    () => buildAvailabilityPreviewState(availabilityConfig.week_duration, answer),
    [answer, availabilityConfig.week_duration]
  );
  const [state, setState] = useState(normalizedInitialState);

  useEffect(() => {
    setState(normalizedInitialState);
  }, [normalizedInitialState]);

  const weekSections = useMemo(
    () =>
      buildAvailabilityWeekSections(
        state,
        availabilityConfig.week_start_day,
        availabilityConfig.week_duration
      ),
    [availabilityConfig.week_duration, availabilityConfig.week_start_day, state]
  );

  const showWeekLabels = weekSections.length > 1;

  const onRowChange = (absoluteOffset: number) => (value: IAvailabilityTableData) => {
    const offsetKey = String(absoluteOffset);
    const newState = { ...state, [offsetKey]: value };
    setState(newState);
    onChange?.(newState);
  };

  const tableHeaderStyling = {
    ...classes.availabilityTableHeader,
    ...(removePadding && { padding: 'unset' })
  };

  return (
    <Box ref={innerRef} sx={{ padding: removePadding ? 0 : 1, overflow: 'auto' }}>
      {weekSections.map((weekSection) => (
        <Box key={weekSection.weekNumber} sx={{ marginBottom: 2 }}>
          {showWeekLabels ? (
            <Box sx={{ fontWeight: 600, marginBottom: 1 }}>Week {weekSection.weekNumber}:</Box>
          ) : null}
          <Table sx={{ backgroundColor: 'inherit', tableLayout: 'auto !important' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderStyling}>
                  <Stack
                    sx={{
                      alignItems: 'center',
                      minWidth: '75px'
                    }}
                  >
                    Payroll Day
                  </Stack>
                </TableCell>
                <TableCell sx={tableHeaderStyling}>
                  <Stack
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    Day
                  </Stack>
                </TableCell>
                <TableCell sx={tableHeaderStyling}>
                  <Stack
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    {availabilityConfig.heading_1}
                  </Stack>
                </TableCell>
                <TableCell sx={tableHeaderStyling}>
                  <Stack
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    {availabilityConfig.heading_2}
                  </Stack>
                </TableCell>
                <TableCell sx={tableHeaderStyling}>
                  <Stack
                    sx={{
                      alignItems: 'center',
                      height: '14px'
                    }}
                  >
                    Shift times
                  </Stack>
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      justifyContent: 'space-around'
                    }}
                  >
                    <Stack
                      sx={{
                        fontSize: '10px',
                        height: '16px'
                      }}
                    >
                      From
                    </Stack>
                    <Stack
                      sx={{
                        fontSize: '10px',
                        height: '16px'
                      }}
                    >
                      To
                    </Stack>
                  </Stack>
                </TableCell>
                {availabilityConfig.allow_break ? (
                  <TableCell sx={tableHeaderStyling}>
                    <Stack
                      sx={{
                        alignItems: 'center'
                      }}
                    >
                      Break
                    </Stack>
                  </TableCell>
                ) : null}
                <TableCell sx={tableHeaderStyling}>
                  <Stack
                    sx={{
                      alignItems: 'center',
                      minWidth: '75px'
                    }}
                  >
                    Paid Hours
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weekSection.rows.map((row) => (
                <DayRow
                  key={row.absoluteOffset}
                  allowBreak={availabilityConfig.allow_break}
                  data={state[String(row.absoluteOffset)]}
                  day={row.day}
                  disabled={disabled}
                  onChange={onRowChange(row.absoluteOffset)}
                  payrollDay={row.payrollDay}
                />
              ))}
              <TableRow>
                <TableCell colSpan={4} sx={classes.availabilityTableBody}>
                  <Stack
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    TOTAL
                  </Stack>
                </TableCell>
                <TableCell
                  colSpan={availabilityConfig.allow_break ? 2 : 1}
                  sx={classes.availabilityTableBody}
                >
                  <Stack
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    {weekSection.totalShifts} Shifts
                  </Stack>
                </TableCell>
                <TableCell sx={classes.availabilityTableBody}>
                  <Stack
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    {weekSection.totalHours}
                  </Stack>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      ))}
      {error ? (
        <Box sx={{ ...sharedClasses.errorBoxCheckbox, marginLeft: '14px' }}>{error}</Box>
      ) : null}
    </Box>
  );
};

export default AvailabilityPreview;
